import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ADPResourceService, CustomFormControl } from 'digital-core';
import { DeviceEligibilityService } from 'src/app/shared/coverage-eligibility/device-eligibility.service';
import { buildDeviceDisplayName } from 'src/app/shared/device/device-display-name.pipe';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { SerialSearchRequestDto } from 'src/app/shared/serial-search/serial-search-request.dto';
import { SerialSearchResponseDto } from 'src/app/shared/serial-search/serial-search-response.dto';
import { SerialSearchService } from 'src/app/shared/serial-search/serial-search.service';
import { format } from 'date-fns';


export interface ImeiResult {
  imei: string;
  imeiLookupResult: SerialSearchResponseDto;
}

export enum ImeiLookupDeviceTemplate {
  Smartphone = 'Smartphone',
  Smartwatch = 'Smartwatch',
  Tablet = 'Tablet'
}

@Component({
  selector: 'pgh-imei-lookup',
  templateUrl: './imei-lookup.component.html',
  styleUrls: ['./imei-lookup.component.scss']
})
export class ImeiLookupComponent implements OnInit {
  @Input() imeiLookupDeviceTypeTemplate: ImeiLookupDeviceTemplate;
  @Input() isOnboardingFlow?: boolean = false;
  @Input() onboardingFlowDevice?: PghUserDeviceBase;
  @Output() viewIMEIInfo = new EventEmitter();
  @Output() nextEvent = new EventEmitter<ImeiResult>();

  public preHeaderTextContext?: { device: string };

  public isSmartphone: boolean = false;
  public isTablet: boolean = false;
  public isSmartwatch: boolean = false;

  public imeiNumberForm: UntypedFormGroup;

  device: PghUserDeviceBase;

  constructor(
    public resourceService: ADPResourceService,
    private selectedDeviceService: SelectedDeviceService,
    private serialSearchService: SerialSearchService,
    private deviceEligibilityService: DeviceEligibilityService,
    private router: Router
  ) { }


  ngOnInit(): void {

    if (this.imeiLookupDeviceTypeTemplate === ImeiLookupDeviceTemplate.Smartphone) {
      this.isSmartphone = true;
    }
    else if (this.imeiLookupDeviceTypeTemplate === ImeiLookupDeviceTemplate.Tablet) {
      this.isTablet = true;
    }
    else if (this.imeiLookupDeviceTypeTemplate === ImeiLookupDeviceTemplate.Smartwatch) {
      this.isSmartwatch = true;
    }

    this.imeiNumberForm = new UntypedFormGroup({
      imeiNumber: new CustomFormControl({ value: this.device?.imei, disabled: false }, [Validators.required,
        this.validateImeiNumber.bind(this)])
    });

    if (this.isOnboardingFlow) {
      this.device = this.onboardingFlowDevice;
    }
    else {
      this.device = this.selectedDeviceService.getSelectedDevice();
    }

    this.preHeaderTextContext = { device: buildDeviceDisplayName(this.device) };
  }

  public submitForm() {
    if (!this.imeiNumberForm.valid) {
      return;
    }

    this.disableEnableInputs(true);

    const searchCriteria: SerialSearchRequestDto = {
      facetCount: 1,
      top: 1000, // Important that it is 1000. We want to make sure we aren't missing any options for the user to select from for their make/model otherwise they will be really confused if the correct options aren't there for their device.
      serialNumber: this.imeiNumberForm.controls.imeiNumber.value,
      client: this.resourceService.getPortalSetting('Onboarding_SerialSearch_Client_Name').Value
    };

    this.serialSearchService.searchForEquipment(searchCriteria).subscribe({
      next: (response) => {
        if (!this.deviceEligibilityService.doesSerialSearchResultHaveDeviceThatIsEligibleForCoverage(response?.result)) {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'Global_Error_Ineligible_For_Coverage' } });
          this.disableEnableInputs(false);
          return;
        }

        if ((response?.result?.value?.length ?? 0) < 1) {
          this.disableEnableInputs(false);
          throw new HttpErrorResponse({
            error: {
              ErrorId: `PGH-UI-${format(new Date(), 'yyyyMMdd-HHmmssSSS')}`,
              Source: 'SerialSearch',
              ErrorCode: 'Ess_Index_Empty'
            }
          });
        }

        this.nextEvent.emit({
          imei: this.imeiNumberForm.controls.imeiNumber.value,
          imeiLookupResult: response,
        });
      },
      error: (error) => {
        this.disableEnableInputs(false);
        throw error;
      }
    });
  }

  showimeiInvalidError = false;
  validateImeiNumber(control: UntypedFormControl) {
    if (control?.value != null) {
      if (!this.isImeiNumberValid(control.value)) {
        if(control.value.length == 15)
          this.showimeiInvalidError = true;
        else
          this.showimeiInvalidError = false;
        return { invalid: true };
      } else {
        this.showimeiInvalidError = false;
      }
    }
  }

  isImeiNumberValid(imei: string): boolean {
    let etal = /^[0-9]{15}$/;

    if (!etal.test(imei)) {
      return false;
    }

    let sum = 0;
    let mul = 2;
    let l = 14;

    for (let i = 0; i < l; i++) {
      let digit = imei.substring(l - i - 1, l - i);

      let tp = parseInt(digit, 10) * mul;

      if (tp >= 10) {
        sum += (tp % 10) + 1;
      } else {
        sum += tp;
      }

      if (mul === 1) {
        mul++;
      } else {
        mul--;
      }
    }

    let chk = (10 - (sum % 10)) % 10;

    if (chk !== parseInt(imei.substring(14, 15), 10)) {
      return false;
    }

    // console.log('Valid IMEI');
    return true;
  }

  handleViewIMEIInfo() {
    this.viewIMEIInfo.emit();
  }

  /**
   * This method will enable / disable the input fields depending on whether
   * your pass a true or a false in. When you pass a true in, then it will
   * disable the inputs. When you pass a false in, then it will enable the
   * inputs.
   *
   * @param disabled When disabled is true, then the input fields will be
   * disabled. When disabled is false, then the input fields will be enabled.
   */
  private disableEnableInputs(disabled: boolean) {
    if (disabled === true) {
      this.imeiNumberForm.disable();
    } else {
      this.imeiNumberForm.enable();
    }
  }

}
